import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  createContext,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import WithdrawalConfirmation from "./pages/WithdrawalConfirmation";
import { getToken, getRole } from "./api";

export const ThemeContext = createContext({
  theme: "dark",
  toggleTheme: () => {},
});

const getInitialTheme = () => localStorage.getItem("theme") || "dark";

function App() {
  const [token, setToken] = useState(getToken());
  const [role, setRole] = useState(getRole());
  const [theme, setTheme] = useState(getInitialTheme);

  // Update auth state when localStorage changes
  useEffect(() => {
    const handleAuthChange = () => {
      setToken(getToken());
      setRole(getRole());
    };

    window.addEventListener("storage", handleAuthChange);
    window.addEventListener("auth-update", handleAuthChange);

    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("auth-update", handleAuthChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.dataset.theme = theme;
    document.body.style.backgroundColor =
      theme === "dark" ? "#0f172a" : "#f8fafc";
    document.body.style.color = theme === "dark" ? "#f8fafc" : "#0f172a";
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const themeValue = useMemo(
    () => ({
      theme,
      toggleTheme,
    }),
    [theme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={themeValue}>
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route
            path="/withdrawal/confirm"
            element={<WithdrawalConfirmation />}
          />
          <Route
            path="/user"
            element={
              token && role === "user" ? <UserDashboard /> : <Navigate to="/" />
            }
          />
          <Route
            path="/admin"
            element={
              token && role === "admin" ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Routes>
      </Router>
    </ThemeContext.Provider>
  );
}

export default App;
